import { Injectable, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

import { BackHistory } from './back-history';
import { ForwardHistory, NavigationStep, stepFromNavigation } from './forward-history';

/**
 * Keeps track of what lies ahead and behind, so the two edge gestures can
 * refuse politely.
 *
 * It listens rather than being told: every navigation passes through the
 * router, including the ones Ionic's own back gesture causes, so nothing has
 * to be wired into the screens themselves. A screen that navigates in a way
 * nobody anticipated is still counted correctly.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService implements OnDestroy {
  private readonly history = new ForwardHistory();
  private readonly behind = new BackHistory();
  /** What each navigation in flight was, kept until it ends or is dropped. */
  private readonly inFlight = new Map<number, { step: NavigationStep; replaces: boolean }>();
  private readonly subscription: Subscription;

  constructor(private readonly router: Router, private readonly location: Location) {
    this.subscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const ended = this.inFlight.get(event.id);
          if (ended) {
            this.behind.record(ended.step, ended.replaces);
          }
          // Whatever started before this one was cancelled or superseded.
          this.inFlight.clear();
          return;
        }
        const step = stepFromNavigation({
          trigger: event.navigationTrigger ?? 'imperative',
          restoredNavigationId: event.restoredState?.navigationId ?? null,
          currentNavigationId: event.id,
        });
        this.history.record(step);
        const extras = this.router.currentNavigation?.()?.extras;
        this.inFlight.set(event.id, { step, replaces: !!(extras?.replaceUrl || extras?.skipLocationChange) });
      });
  }

  /** Whether a step back would land on a page of this session. */
  get canGoBack(): boolean {
    return this.behind.canGoBack;
  }

  get canGoForward(): boolean {
    return this.history.canGoForward;
  }

  /**
   * Steps forward, or does nothing at all.
   *
   * Answers whether it moved, so a gesture can decide what to show. Silence
   * from a gesture is the thing to avoid: a finger that moves while the screen
   * does not reads as a broken application, not as a refusal.
   */
  goForward(): boolean {
    if (!this.canGoForward) {
      return false;
    }
    this.location.forward();
    return true;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
